import { useNavigate, useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import Input from 'MediaManager/components/Form/Input'
import JSONEditor from 'MediaManager/components/JSONEditor'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getVendors } from 'MediaManager/services'
import {
  deleteDesignLayout,
  getDesignLayout,
  getDesignLayoutsConfigurationOptionsFields,
  upsertDesignLayout,
  upsertDesignLayoutConfigurationOptions,
} from 'MediaManager/services/designs'
import { getUnitType } from 'MediaManager/services/property'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import Button from 'shared/components/Button'
import Checkbox from 'shared/components/Checkbox'
import DataTable from 'shared/components/DataTable'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import { snakeCaseToTitleCase } from 'shared/helpers/general'
import { useRequest } from 'shared/hooks/useRequest'
import { getUnitTypeDesignLayouts } from 'shared/helpers/levels'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'
import SelectPackage from '../components/SelectPackage'

function DesignLayoutsEditScreen(props) {
  const { line, id } = useSearch()

  const [resultValue, setResultValue] = useState()

  const getConfigurationOptionsFields = useCallback(async () => {
    const fields = await getDesignLayoutsConfigurationOptionsFields()
    return fields.filter((f) => !['id', 'design_layout'].includes(f.name))
  }, [line])

  const { data: configurationFields } = useRequest(getConfigurationOptionsFields)

  const getVendorsData = useCallback(async () => {
    const { data } = await getVendors()
    return data.vendors
  }, [line])

  const { data: vendors } = useRequest(getVendorsData)

  const getData = useCallback(async () => {
    const { data } = await getUnitType(line)
    return data.unit_types[0]
  }, [line])

  const { data, loading, refresh } = useRequest(getData)

  const designLayout = useMemo(() => {
    if (data) {
      return getUnitTypeDesignLayouts(data).find((i) => i.id === Number(id))
    }
  }, [data, id])

  const levelOptions = useMemo(() => {
    if (data) {
      return data.unit_type_levels.map((d) => ({
        label: d.floor_number,
        value: d.id,
      }))
    }
  }, [data])

  const vendorOptions = useMemo(() => {
    if (vendors) {
      return vendors.map((v) => ({
        label: v.full_name,
        value: v.id,
      }))
    }
    return []
  }, [vendors])

  const navigate = useNavigate()

  useEffect(() => {
    if (data) {
      formik.setValues({
          level: designLayout?.unit_type_level_id,
          name: designLayout?.name,
          vendor: designLayout?.main_vendor?.id,
          package: designLayout?.package,
          configuration_options: {
            ...(configurationFields || []).reduce((acc, field) => {
              acc[field.name] = designLayout?.design_layouts_configuration_options?.[field.name] || false
              return acc
            }, {}),
          },
        })
        try {
          const edits = sessionStorage.getItem('design_layouts_edit')
          const vals = JSON.parse(edits)
          Object.keys(vals).forEach((key) => {
            formik.setFieldValue(key, vals[key])
          })
        } catch (error) {}
    }
  }, [data, designLayout])

  const validationSchema = Yup.object().shape({
    level: Yup.number().nullable(),
    vendor: Yup.number().required(errorMessages.required),
    name: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    // initialValues: sessionStorage.getItem('design_layouts_edit') ? JSON.parse(sessionStorage.getItem('design_layouts_edit')) : {},
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        const { data } = await upsertDesignLayout({
          ...(id ? { id } : {}),
          name: formik.values.name,
          main_vendor_id: formik.values.vendor,
          design_json: formik.values.design_json,
          unit_type_id: Number(line),
          ...(formik.values.package ? { package_id: formik.values.package.id } : { package_id: null }),
        })

        const designLayoutId = data?.upsert_design_layouts[0]?.id || id
        await upsertDesignLayoutConfigurationOptions({
          ...configurationFields.reduce((acc, field) => {
            acc[field.name] = false
            return acc
          }, {}),
          ...(designLayoutId ? { design_layout_id: designLayoutId } : {}),
          ...(configurationFields || []).reduce((acc, field) => {
            acc[field.name] = formik.values.configuration_options?.[field.name] || false
            return acc
          }, {}),
        })
        if (!id) {
          navigate({ to: `/design_layouts/edit?line=${line}&id=${data.upsert_design_layouts[0].id}` })
        }
        setResultValue('success')
      } catch (error) {
        setResultValue('error')
      } finally {
        sessionStorage.removeItem('design_layouts_edit')
      }
    },
  })

  const isValid = useMemo(() => {
    return Object.values(formik.errors).every((v) => !v)
  }, [formik, data])

  function getFieldError(field) {
    return formik.errors[field] && formik.touched[field] ? formik.errors[field] : null
  }

  async function onDelete() {
    try {
      await deleteDesignLayout(id)
      setResultValue('success')
    } catch (error) {
      setResultValue('error')
    }
  }

  const [designLayoutData, setDesignLayoutData] = useState({})

  useEffect(() => {
    if (id) {
      ;(async () => {
        const { data } = await getDesignLayout(id)
        setDesignLayoutData(data.design_layouts[0])
      })()
    }
  }, [id])

  async function onConfigurationOptionUpdate(key, value) {
    await upsertDesignLayoutConfigurationOptions({
      design_layout_id: id,
      [key]: value,
    })
    toast.success('Configuration flags updated', { theme: 'colored' })
  }

  function onValueChange(key, value) {
    const edits = sessionStorage.getItem('design_layouts_edit')
    const parsedEdits = edits ? JSON.parse(edits) : {}
    parsedEdits[key] = value
    sessionStorage.setItem('design_layouts_edit', JSON.stringify(parsedEdits))
  }

  return (
    <ScreenLayout title={`${id ? 'Update' : 'Add'} design layout`}>
      {resultValue && (
        <ActionSuccessModal
          action={`Design Layout`}
          successText="has been successfully upserted."
          errorText="upserting failed. Please try again."
          state={resultValue}
          error={'Please try again later.'}
          skipConfirm
          skipBack
          onClose={() => {
            setResultValue(null)
          }}
        />
      )}
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      {!loading && (
        <>
          <div>
            <Input label="Property" disabled value={data.property.property_name} />
            <Input wrapClassName="mt-[16px]" label="Line" disabled value={data.name} />
          </div>
          <div className="mt-[16px]">
            <form onSubmit={formik.handleSubmit}>
              <div className="grid grid-cols-2 gap-[16px]">
                <div className="col-span-2">
                  <Select
                    options={levelOptions}
                    value={levelOptions.find((l) => l.value === formik.values.level)}
                    label="Level"
                    error={getFieldError('level')}
                    onChange={(val) => {
                      formik.setFieldValue('level', val.value)
                      onValueChange('level', val.value)
                    }}
                  />
                </div>
                <div className="col-span-1">
                  <JSONEditor
                    label="Upload Design JSON"
                    json={designLayoutData.design_json}
                    onChange={(json) => formik.setFieldValue('design_json', json)}
                    value={formik.values.design_json}
                    onDelete={() => {
                      formik.setFieldValue('design_json', null)
                      setDesignLayoutData({ ...designLayoutData, design_json: null })
                    }}
                  />
                </div>
                <div className="col-span-1">
                  <div className="text-sm my-[16px] opacity-50">Packages</div>
                  <div className="flex items-center bg-[#F5F5F5] w-full rounded-[10px] p-[16px]">
                    <Button
                      rounded
                      size="small"
                      onClick={() => {
                        navigate({
                          to: `/packages/create`,
                          search: {
                            property_id: data.property.id,
                            line_id: data.id,
                            design_layout_id: id,
                            level_id: formik.values.level,
                            vendor_id: formik.values.vendor,
                          },
                        })
                      }}
                    >
                      Create package
                    </Button>
                    <div className="ml-[16px]">
                      <SelectPackage
                        pack={formik.values.package}
                        vendorId={formik.values.vendor}
                        onPicked={(pack) => {
                          formik.setFieldValue('package', pack)
                        }}
                      />
                    </div>
                    <span className="ml-[16px] opacity-50">
                      {formik.values.package ? `${formik.values.package.name}` : `No package created`}
                    </span>
                  </div>
                </div>
                <div className="col-span-2">
                  <Input
                    wrapClassName="mt-[16px]"
                    label="Design layout name"
                    name="name"
                    error={getFieldError('name')}
                    onChange={(e) => {
                      formik.handleChange(e)
                      onValueChange('name', e.target.value)
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Select
                    options={vendorOptions}
                    required
                    error={getFieldError('vendor')}
                    value={vendorOptions.find((v) => v.value === formik.values.vendor)}
                    onChange={(val) => {
                      formik.setFieldValue('vendor', val.value)
                      onValueChange('vendor', val.value)
                    }}
                    label="Main vendor"
                  />
                </div>
              </div>
              <div className="my-[32px]">
                <div className="text-sm mb-2 opacity-50">Configuration options</div>
                <div className="grid grid-cols-4 gap-[16px]">
                  {configurationFields.map((s) => (
                    <div className="flex items-center" key={s.name}>
                      <Checkbox
                        name={s.name}
                        checked={formik.values.configuration_options?.[s.name]}
                        onChange={() => {
                          if (id) {
                            onConfigurationOptionUpdate(s.name, !formik.values.configuration_options[s.name])
                          }
                          formik.setFieldValue(
                            `configuration_options.${s.name}`,
                            !formik.values.configuration_options[s.name],
                          )
                          onValueChange(`configuration_options.${s.name}`, !formik.values.configuration_options[s.name])
                        }}
                      />
                      <div className="ml-3 truncate">{snakeCaseToTitleCase(s.name)}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={`flex justify-end items-center mt-[16px] ${
                  formik.isSubmitting ? 'pointer-events-none  opacity-50' : ''
                }`}
              >
                {id && (
                  <Button rounded variant="secondary" onClick={onDelete} className="mr-[16px]">
                    Delete
                  </Button>
                )}
                <Button
                  rounded
                  variant="secondary"
                  onClick={() => {
                    sessionStorage.removeItem('design_layouts_edit')
                    window.history.back()
                  }}
                >
                  Cancel
                </Button>
                <Button loading={formik.isSubmitting} disabled={!isValid} rounded className="ml-[16px]" type="submit">
                  {id ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </ScreenLayout>
  )
}

export default DesignLayoutsEditScreen
